import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { AreaChartDateLabelUnit } from '../../../../ui/components/pure/charts/area/area-chart.component';
import { KioskConnectionState, KioskStateEvent } from '../types/kiosk.types';
import { Colors } from '../../../../ui/theme/colors';

dayjs.extend(duration);

/**
 * Calculate and format offline duration from last updated timestamp
 */
export function calculateOfflineDuration(lastUpdated: Date): string {
  const now = dayjs();
  const lastSeen = dayjs(lastUpdated);
  const diff = now.diff(lastSeen);

  if (diff < 60000) {
    // Less than 1 minute
    return 'Just now';
  }

  const durationObj = dayjs.duration(diff);

  if (diff < 3600000) {
    // Less than 1 hour
    const minutes = Math.floor(durationObj.asMinutes());
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  if (diff < 86400000) {
    // Less than 1 day
    const hours = Math.floor(durationObj.asHours());
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }

  const days = Math.floor(durationObj.asDays());
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/**
 * Format kiosk connection status for display
 */
export function formatKioskStatus(status: KioskConnectionState | string): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'disconnected':
      return 'Disconnected';
    default:
      return status || 'Unknown';
  }
}

/**
 * Check if kiosk is currently active
 */
export function isKioskActive(status: KioskConnectionState | string): boolean {
  return status === 'connected';
}

/**
 * Transform connection events into AreaChart format
 * Shows connection state over time (connected = 1, disconnected = 0)
 */
export function transformConnectionEventsForChart(
  events: KioskStateEvent[],
  dateRange?: { fromDate?: Date; toDate?: Date }
): {
  labels: number[];
  datasets: Array<{
    label: string;
    data: { x: number; y: number }[];
    fill: boolean;
    backgroundColor: string;
    borderColor: string;
    stepped?: 'before' | 'after' | 'middle' | boolean;
    tension?: number;
    pointRadius?: number;
    pointHoverRadius?: number;
    borderWidth?: number;
  }>;
  dateLabelUnit: AreaChartDateLabelUnit;
} {
  if (!events || events.length === 0) {
    return {
      labels: [],
      datasets: [],
      dateLabelUnit: AreaChartDateLabelUnit.Day
    };
  }

  // Sort events by start time
  const sortedEvents = [...events].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  // Determine date range
  const fromDate = dateRange?.fromDate ? dayjs(dateRange.fromDate) : dayjs(sortedEvents[0].startTime);
  const lastEvent = sortedEvents[sortedEvents.length - 1];
  const toDate = dateRange?.toDate ? dayjs(dateRange.toDate) : dayjs(lastEvent.endTime || new Date());

  const rangeDays = toDate.diff(fromDate, 'day');
  const rangeHours = toDate.diff(fromDate, 'hour');

  // Determine appropriate date label unit
  let dateLabelUnit: AreaChartDateLabelUnit;
  if (rangeDays > 30) {
    dateLabelUnit = AreaChartDateLabelUnit.Month;
  } else if (rangeHours > 24) {
    dateLabelUnit = AreaChartDateLabelUnit.Day;
  } else {
    dateLabelUnit = AreaChartDateLabelUnit.Hour;
  }

  // Build data points
  const dataPoints: { x: number; y: number }[] = [];
  const labels: number[] = [];

  // Add initial point
  const startTime = fromDate.toDate().getTime();
  labels.push(startTime);
  dataPoints.push({ x: startTime, y: 0 }); // Start disconnected

  // Process each event
  for (const event of sortedEvents) {
    const isConnected = isKioskActive(event.connectionState);
    const startTime = dayjs(event.startTime).toDate().getTime();
    labels.push(startTime);
    dataPoints.push({ x: startTime, y: isConnected ? 1 : 0 });
  }

  // Add final point
  const endTime = toDate.toDate().getTime();
  if (!labels.includes(endTime)) {
    labels.push(endTime);
    const isConnected = isKioskActive(lastEvent.connectionState);
    dataPoints.push({ x: endTime, y: isConnected ? 1 : 0 });
  }

  // Convert hex color to rgba for background with opacity
  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return {
    labels,
    datasets: [
      {
        label: 'Connection Status',
        data: dataPoints,
        fill: true,
        backgroundColor: hexToRgba(Colors.collectedGreen, 0.2),
        borderColor: Colors.collectedGreen,
        stepped: 'before', // Enable step interpolation for connection state changes
        tension: 0, // Ensure straight line segments (required for step graphs)
        pointRadius: 0, // Hide points to prevent triangles
        pointHoverRadius: 0,
        borderWidth: 2
      }
    ],
    dateLabelUnit
  };
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(date: Date | string): string {
  return dayjs(date).format('MMM D, YYYY h:mm A');
}

/**
 * Calculate and format duration between two timestamps for an event
 */
export function calculateEventDuration(event: { startTime: string; endTime?: string }): string {
  if (!event.endTime) {
    return 'Ongoing';
  }
  const start = dayjs(event.startTime);
  const end = dayjs(event.endTime);
  const diff = end.diff(start);
  const durationObj = dayjs.duration(diff);

  if (diff < 60000) {
    return `${Math.floor(durationObj.asSeconds())}s`;
  } else if (diff < 3600000) {
    return `${Math.floor(durationObj.asMinutes())}m`;
  } else if (diff < 86400000) {
    return `${Math.floor(durationObj.asHours())}h`;
  } else {
    return `${Math.floor(durationObj.asDays())}d`;
  }
}
